import tinycolor from 'tinycolor2'
import clone from 'lodash/clone'
import type { Address, BpOrder, BpUserAddress, DeliveryAddress } from '~/definitions/AllDefinitions'

export function getTheme(hex: string, name: string) {
  const color = tinycolor(clone(hex))
  const isLight = color.isLight()
  let val: number
  if (isLight) {
    val = 255 - color.getBrightness()
  } else {
    val = 100 - ((255 - color.getBrightness()) / 10)
  }
  val = Math.round(val)
  if (val >= 95) {
    val = 950
  } else if (val <= 5) {
    val = 50
  } else {
    val = Math.round(val / 10) * 100
  }
  const theme = [
    50,
    100,
    200,
    300,
    400,
    500,
    600,
    700,
    800,
    900,
    950,
  ]
  let ret = `--tw-color-${name}: ${convertToRgb(hex)};\r\n`
  ret += `--tw-color-${name}-contrast: `
  if (isLight) {
    ret += '30 41 59'
  } else {
    ret += '255 255 255'
  }
  ret += ';\r\n'
  ret += `--tw-color-${name}-is-light: ${isLight ? 'true' : 'false'};\r\n`
  theme.forEach((k) => {
    const difference = (val - k) / 10
    const newColor = tinycolor(clone(hex))
    let hexString
    if (difference > 0) {
      hexString = newColor.brighten(difference).toString()
    } else if (difference < 0) {
      hexString = newColor.darken(difference).toString()
    } else {
      hexString = clone(hex)
    }
    ret += `--tw-color-${name}-${k}: ${convertToRgb(hexString)};\r\n`
  })
  return ret
}
export function convertToRgb(hex: string) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i
    .exec(hex)
  if (result) {
    return parseInt(result[1], 16) + ' ' +
      parseInt(result[2], 16) + ' ' + parseInt(result[3], 16)
  }
  return null
}
export function isLast(items: Array<any>, idx: number) {
  return idx === items.length - 1
}

export function getFormattedAddress(data: BpUserAddress | BpOrder | Address | DeliveryAddress | null) {
  let formattedAddress = ''
  if (data) {
    if (data.addressOne) {
      formattedAddress += data.addressOne
    }
    if (data.addressTwo) {
      if (formattedAddress.length) {
        formattedAddress += ' '
      }
      formattedAddress += data.addressTwo
    }
    if (data.addressThree) {
      if (formattedAddress.length) {
        formattedAddress += ' '
      }
      formattedAddress += data.addressThree
    }
    if (data.city) {
      if (formattedAddress.length) {
        formattedAddress += ', '
      }
      formattedAddress += data.city
    }
    if (data.state) {
      if (formattedAddress.length) {
        formattedAddress += ', '
      }
      formattedAddress += data.state
    }
    if (data.postalCode) {
      if (formattedAddress.length) {
        formattedAddress += ' '
      }
      formattedAddress += data.postalCode
    }
  }
  return formattedAddress.trim()
}
export function updateAddress(input: Address | BpOrder | BpUserAddress | DeliveryAddress,
  output: BpOrder | DeliveryAddress) {
  if (input.addressOne) {
    output.addressOne = clone(input.addressOne)
  } else {
    output.addressOne = null
  }
  if (input.addressTwo) {
    output.addressTwo = clone(input.addressTwo)
  } else {
    output.addressTwo = null
  }
  if (input.addressThree) {
    output.addressThree = clone(input.addressThree)
  } else {
    output.addressThree = null
  }
  if (input.city) {
    output.city = clone(input.city)
  } else {
    output.city = null
  }
  if (input.state) {
    output.state = clone(input.state)
  } else {
    output.state = null
  }
  if (input.country) {
    output.country = clone(input.country)
  } else {
    output.country = 'US'
  }
  if (input.sublocality) {
    output.sublocality = clone(input.sublocality)
  } else {
    output.sublocality = null
  }
  if (input.latitude) {
    output.latitude = clone(input.latitude)
  } else {
    output.latitude = 0
  }
  if (input.longitude) {
    output.longitude = clone(input.longitude)
  } else {
    output.longitude = 0
  }
  return output
}
export function copyAddressFromOrder(input: BpOrder) {
  const output = {
    id: 0,
    isPrimaryBilling: false,
    isPrimaryDelivery: false,
  } as DeliveryAddress
  if (input.firstName) {
    output.firstName = clone(input.firstName)
  } else {
    output.firstName = null
  }
  if (input.lastName) {
    output.lastName = clone(input.lastName)
  } else {
    output.lastName = null
  }
  if (input.email) {
    output.email = clone(input.email)
  } else {
    output.email = null
  }
  if (input.phoneFormatted) {
    output.phoneFormatted = clone(input.phoneFormatted)
  }
  if (input.phoneUnformatted) {
    output.phoneUnformatted = clone(input.phoneUnformatted)
  }
  if (input.phoneType) {
    output.phoneType = clone(input.phoneType)
  }
  if (input.phoneCountry) {
    output.phoneCountry = clone(input.phoneCountry)
  }
  return updateAddress(input, output) as DeliveryAddress
}

export function updateOrderFromAddress(input: DeliveryAddress | Address | BpUserAddress, output: BpOrder) {
  return updateAddress(input, output) as BpOrder
}

export function toFixedNoRound(num: string | number = 0, fixed = 2, parse = false) {
  const re = new RegExp('^-?\\d+(?:.\\d{0,' + (fixed || -1) + '})?')
  if (typeof num !== 'string') {
    num = '' + num
  }
  const matches = (num || 0).toString().match(re)
  const match = matches ? matches[0] : '0'
  const hasDot = match.includes('.')
  const value = hasDot ? match : `${match}.${'0'.repeat(fixed)}`
  if (parse) {
    return parseFloat(value)
  }
  return value
}
export function scrollToElementById(id: string, offset?: number, scrollContainer?: HTMLElement) {
  const elem = document.getElementById(id)
  if (elem) {
    if (offset) {
      // Get the element's top position relative to the viewport
      const elemPos = elem.getBoundingClientRect().top
      // Get the current scroll position using scrollY
      const scrollY = scrollContainer ? scrollContainer.scrollTop : window.scrollY
      const offsetPos = scrollY + elemPos + offset
      if (scrollContainer) {
        scrollContainer.scrollTo({
          behavior: 'smooth',
          top: offsetPos,
        })
      } else {
        window.scrollTo({
          behavior: 'smooth',
          top: offsetPos,
        })
      }
    } else {
      elem.scrollIntoView({
        behavior: 'smooth',
      })
    }
  }
}
